import type {
  RealtimeMessageHandler,
  RealtimeAdapter,
  RealtimeSubscription,
  RealtimePayload,
  RealtimeMetrics,
} from "./types";
import { getPermissionViolationFingerprint } from "./types";
import { captureException, addBreadcrumb } from "@web-monorepo/telemetry";

let schemasPromise: Promise<typeof import("@web-monorepo/api-types/zodSchemas/realtime/index") | null> | null = null;
function loadSchemas() {
  schemasPromise ??= import("@web-monorepo/api-types/zodSchemas/realtime/index").catch((err) => {
    addBreadcrumb({
      category: "realtime",
      message: "Zod schema load failed; falling back to structural guard",
      level: "warning",
      data: { error: String(err) },
    });
    return null;
  });
  return schemasPromise;
}

/**
 * Resolve the lazy realtime-schema import up front. Tests only: the message loop
 * awaits `loadSchemas()` before dispatching, so a test that publishes and then
 * asserts on delivery otherwise races this cold dynamic import against its own
 * `waitFor` budget. Call it in `beforeAll` to pay the import where no timeout is
 * running. Keep the specifier above as the single source of truth — callers must
 * not re-declare it.
 */
export async function preloadRealtimeSchemas(): Promise<void> {
  await loadSchemas();
}

// Minimal structural guard: handlers discriminate on `action` and gracefully
// skip unknown types, so a full Zod parse is not required for the fallback.
function isRealtimePayload(v: unknown): v is RealtimePayload {
  return typeof v === "object" && v !== null && typeof (v as Record<string, unknown>).action === "string";
}

type Schemas = typeof import("@web-monorepo/api-types/zodSchemas/realtime/index");

/**
 * Validate an incoming payload, degrading to the structural guard rather than
 * discarding the event when the schema rejects it.
 *
 * A throwing `parse` here meant one field the server doesn't send — or sends in
 * a shape the synced schema hasn't caught up with — silently killed an entire
 * event class: the throw was swallowed by the subscription loop's catch, so the
 * only trace was a Sentry entry with no payload attached. That is how the
 * mass-message completion signal went missing while every per-thread event on
 * the same subject kept working.
 *
 * Falling back is the same posture this module already takes when the schemas
 * fail to load (see `isRealtimePayload`): handlers discriminate on `action` and
 * skip what they don't recognise, so a strict schema is a diagnostic aid rather
 * than a delivery gate. The failure is reported with the failing paths so schema
 * drift stays visible instead of turning into lost events.
 */
function parseMessage(json: unknown, subject: string, schemas: Schemas | null): RealtimePayload | null {
  if (!schemas) {
    return isRealtimePayload(json) ? json : null;
  }
  const schema = subject.startsWith("classroom.")
    ? schemas.ClassroomRealtimeMessageSchema
    : schemas.RealtimeMessageSchema;

  const result = schema.safeParse(json);
  if (result.success) {
    return result.data;
  }

  if (subject.startsWith("user.")) {
    const classroomResult = schemas.ClassroomRealtimeMessageSchema.safeParse(json);
    if (classroomResult.success) {
      return classroomResult.data;
    }
  }

  const shape = typeof json === "object" && json !== null ? (json as { action?: unknown }) : undefined;
  captureException(new Error(`Realtime payload failed schema validation for action=${getAction(shape)}`), {
    extra: {
      subject,
      // Paths and codes only — never values, since payloads can carry names.
      issues: result.error.issues.map((issue) => `${issue.path.join(".") || "(root)"}: ${issue.code}`),
      payloadKeys: shape ? Object.keys(shape) : [],
    },
    tags: { component: "RealtimeClient", operation: "parseMessage" },
  });

  return isRealtimePayload(json) ? json : null;
}

export interface RealtimeClientOptions {
  onSubscriptionError?: (error: Error) => void;
  metrics?: RealtimeMetrics;
}

function getAction(data: { action?: unknown } | null | undefined): string {
  const action = data?.action;
  return typeof action === "string" && action.length > 0 ? action : "unknown";
}

export class RealtimeClient {
  #adapter: RealtimeAdapter;
  #handlers = new Map<string, Set<RealtimeMessageHandler>>();
  #abortControllers = new Map<string, AbortController>();
  #textDecoder = new TextDecoder();
  #onSubscriptionError?: (error: Error) => void;
  #metrics?: RealtimeMetrics;

  constructor(adapter: RealtimeAdapter, options?: RealtimeClientOptions) {
    this.#adapter = adapter;
    this.#onSubscriptionError = options?.onSubscriptionError;
    this.#metrics = options?.metrics;
  }

  async init() {
    await this.#adapter.init();
  }

  subscribe(topic: string, onMessage: RealtimeMessageHandler): void {
    const existingHandlers = this.#handlers.get(topic);

    if (existingHandlers) {
      existingHandlers.add(onMessage);
      return;
    }

    this.#handlers.set(topic, new Set<RealtimeMessageHandler>([onMessage]));

    const abortController = new AbortController();
    this.#abortControllers.set(topic, abortController);

    const sub = this.#adapter.subscribe(topic);
    void this.#handleSubscriptionMessages(sub, topic, abortController.signal);
  }

  unsubscribe(topic: string, onMessage: RealtimeMessageHandler): void {
    const handlers = this.#handlers.get(topic);
    if (!handlers) {
      return;
    }

    if (!handlers.delete(onMessage)) {
      return;
    }

    if (handlers.size === 0) {
      this.#handlers.delete(topic);
      this.#abortControllers.get(topic)?.abort();
      this.#abortControllers.delete(topic);
      this.#adapter.unsubscribe(topic);
    }
  }

  publish(topic: string, data: Record<string, unknown>): void {
    const encoded = new TextEncoder().encode(JSON.stringify(data));
    this.#adapter.publish(topic, encoded);
    this.#metrics?.increment("nats.send", { action: getAction(data) });
  }

  async close() {
    for (const abortController of this.#abortControllers.values()) {
      abortController.abort();
    }
    this.#abortControllers.clear();
    this.#handlers.clear();
    await this.#adapter.close();
  }

  async #handleSubscriptionMessages(sub: RealtimeSubscription, topic: string, signal: AbortSignal): Promise<void> {
    // Kick off schema loading eagerly so it's ready by the time the first
    // message arrives, but don't block loop entry — subscription errors that
    // fire before any messages must still reach the outer catch.
    void loadSchemas();
    let schemas: Schemas | null | undefined;
    try {
      for await (const msg of sub) {
        if (signal.aborted) {
          break;
        }
        try {
          const raw = this.#textDecoder.decode(msg.data);
          const json = JSON.parse(raw);

          schemas ??= await loadSchemas();
          const parsed = parseMessage(json, msg.subject, schemas);
          if (parsed === null) continue;

          this.#metrics?.increment("nats.receive", { action: getAction(parsed) });

          const handlers = this.#handlers.get(msg.subject);
          if (handlers) {
            for (const handler of handlers) {
              (async () => {
                await handler(parsed);
              })().catch((handlerErr) => {
                captureException(handlerErr, {
                  extra: {
                    topic: msg.subject,
                  },
                  tags: {
                    component: "RealtimeClient",
                    operation: "handler",
                  },
                });
              });
            }
          } else {
            captureException(new Error("No handlers found for topic"), {
              extra: {
                topic: msg.subject,
              },
              tags: {
                component: "RealtimeClient",
                operation: "handleSubscriptionMessages",
              },
            });
          }
          // eslint-disable-next-line no-catch-all/no-catch-all -- pre-existing violation
        } catch (err) {
          captureException(err, {
            extra: { topic },
            tags: { component: "RealtimeClient", operation: "messageProcessing" },
          });
        }
      }
      // eslint-disable-next-line no-catch-all/no-catch-all -- pre-existing violation
    } catch (err) {
      captureException(err, {
        extra: { topic },
        tags: { component: "RealtimeClient", operation: "subscriptionError" },
        fingerprint: getPermissionViolationFingerprint(err),
      });
      this.#onSubscriptionError?.(err instanceof Error ? err : new Error(String(err)));
    }
  }
}
